import React, { useState } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const validateField = (name, value) => {
  let error = '';
  if (name === 'name') {
    if (!value.trim()) error = 'Name is required';
    else if (value.length > 120) error = 'Name cannot exceed 120 characters';
  }
  if (name === 'country') {
    if (!value.trim()) error = 'Country is required';
    else if (value.length > 100) error = 'Country cannot exceed 100 characters';
  }
  if (name === 'contact_email') {
    if (!value.trim()) error = 'Contact Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Enter a valid email address';
    else if (value.length > 120) error = 'Contact Email cannot exceed 120 characters';
  }
  if (name === 'bank') {
    if (value && value.length > 100) error = 'Bank cannot exceed 100 characters';
  }
  if (name === 'fax_no') {
    if (value && value.length > 50) error = 'Fax No cannot exceed 50 characters';
  }
  if (name === 'website') {
    if (value && value.length > 120) error = 'Website cannot exceed 120 characters';
    else if (value && !/^((https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?)$/.test(value)) error = 'Enter a valid website (e.g. www.example.com)';
  }
  if (name === 'parent_company') {
    if (value && value.length > 120) error = 'Parent Company cannot exceed 120 characters';
  }
  if (name === 'currency') {
    if (value && value.length > 20) error = 'Currency cannot exceed 20 characters';
  }
  if (name === 'account_no') {
    if (value && value.length > 50) error = 'Account No cannot exceed 50 characters';
  }
  if (name === 'swift_code') {
    if (value && value.length > 50) error = 'SWIFT Code cannot exceed 50 characters';
  }
  if (name === 'ad_code') {
    if (value && value.length > 30) error = 'AD Code cannot exceed 30 characters';
  }
  if (name === 'iec_code') {
    if (value && value.length > 30) error = 'IEC Code cannot exceed 30 characters';
  }
  if (name === 'address') {
    if (value && value.length > 255) error = 'Address cannot exceed 255 characters';
  }
  if (name === 'pincode') {
    if (value && value.length > 45) error = 'Pincode cannot exceed 45 characters';
  }
  return error;
};

const validateForm = (formData) => {
  const errors = {};
  errors.name = validateField('name', formData.name);
  errors.country = validateField('country', formData.country);
  errors.contact_email = validateField('contact_email', formData.contact_email);
  errors.bank = validateField('bank', formData.bank);
  errors.fax_no = validateField('fax_no', formData.fax_no);
  errors.website = validateField('website', formData.website);
  errors.parent_company = validateField('parent_company', formData.parent_company);
  errors.currency = validateField('currency', formData.currency);
  errors.account_no = validateField('account_no', formData.account_no);
  errors.swift_code = validateField('swift_code', formData.swift_code);
  errors.ad_code = validateField('ad_code', formData.ad_code);
  errors.iec_code = validateField('iec_code', formData.iec_code);
  errors.address = validateField('address', formData.address);
  errors.pincode = validateField('pincode', formData.pincode);
  // Remove empty errors
  Object.keys(errors).forEach((key) => {
    if (!errors[key]) delete errors[key];
  });
  return errors;
};

const SupplierForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    contact_email: '',
    bank: '',
    fax_no: '',
    website: '',
    parent_company: '',
    currency: '',
    account_no: '',
    swift_code: '',
    ad_code: '',
    iec_code: '',
    address : '', 
    pincode : '',  
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      toast.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((msg) => {
        if (msg) toast.error(msg);
      });
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/supplier-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Supplier Registered Successfully!');
        setFormData({
          name: '', country: '', contact_email: '', bank: '',  fax_no: '', 
          website: '', parent_company: '', currency: '', account_no: '', swift_code: '', 
          ad_code: '', iec_code: '', address: '', pincode: ''
        });
      } else {
        toast.error(data.error || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Server Error');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-4 bg-white">
      <ToastContainer />
      <div className="flex justify-between items-center border-b pb-3 mb-5">
        <h2 className="text-[15px] font-medium text-gray-800">Supplier Registration</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#15803d] border border-[#15803d] px-3 py-1 text-[12px] rounded font-normal hover:bg-[#166534] hover:text-white hover:border-[#166534] transition"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" required autoComplete="off" maxLength={120} />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Country</label>
            <input type="text" name="country" value={formData.country} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" required autoComplete="off" maxLength={100} />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Contact Email</label>
            <input type="email" name="contact_email" value={formData.contact_email} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" required autoComplete="off" maxLength={120} />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Bank</label>
            <input type="text" name="bank" value={formData.bank} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={100} />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Fax No</label>
            <input type="text" name="fax_no" value={formData.fax_no} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={50} />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Website</label>
            <input type="text" name="website" value={formData.website} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={120} />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Parent Company</label>
            <input type="text" name="parent_company" value={formData.parent_company} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={120} />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Currency</label>
            <input type="text" name="currency" value={formData.currency} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={20} />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Account No</label>
            <input type="text" name="account_no" value={formData.account_no} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={50} />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">SWIFT Code</label>
            <input type="text" name="swift_code" value={formData.swift_code} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={50} />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">AD Code</label>
            <input type="text" name="ad_code" value={formData.ad_code} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={30} />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">IEC Code</label>
            <input type="text" name="iec_code" value={formData.iec_code} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={30} />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Address</label>
            <input type="text" name="address" value={formData.address} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={255} />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Pincode</label>
            <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" maxLength={45} />
          </div>
        </div>
        <div className="pt-2 flex items-center gap-3">
          <button type="submit" className="px-3 py-1.5 text-[12px] bg-[#15803d] text-white font-normal rounded border border-[#15803d] hover:bg-[#166534] transition disabled:opacity-60" style={{ minWidth: '110px' }} disabled={loading}>
            {loading ? 'Saving...' : 'Save Supplier'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SupplierForm;